
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useUser } from '@/contexts/UserContext';
import { OpsEndpointCard } from '@/components/ops/OpsEndpointCard';
import { TimestampDisplay } from '@/components/ops/TimestampDisplay';
import { EmptyState } from '@/components/ops/EmptyState';
import { LoadingSpinner } from '@/components/ops/LoadingSpinner';
import { OpsErrorBoundary } from '@/components/ops/OpsErrorBoundary';
import { Camera } from 'lucide-react';

const API_BASE = import.meta.env.VITE_RAG_API_URL;

interface RuntimeSnapshot {
    semantic_threshold: number;
    cosine_threshold: number;
    min_chunks: number;
    rerank_enabled: boolean;
    rerank_fallback: string;
    policy_applied: string;
    intent_applied: string | null;
    timestamp: number;
    captured_at: string;
}

export function RuntimeStatePage() {
    const { session } = useUser();
    const [data, setData] = useState<RuntimeSnapshot | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        if (session?.access_token) {
            fetchData();
        } else {
            setLoading(false);
            setError("Authentication required");
        }
        return () => {
            isMounted.current = false;
        };
    }, [session]);

    const fetchData = async () => {
        if (!session?.access_token) return;

        try {
            setLoading(true);
            setError(null);
            const res = await axios.get(`${API_BASE}/ops/retrieval/runtime_snapshot`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
                timeout: 5000
            });

            if (isMounted.current) {
                setData(res.data);
            }
        } catch (err: any) {
            if (isMounted.current) {
                if (err.response?.status === 503) {
                    setData(null);
                    setError("Waiting for first query");
                } else if (err.response?.status === 401) {
                    setError("Unauthorized");
                } else if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
                    setError("Backend Offline (Connection Refused)");
                } else {
                    setError(err.message || "Failed to fetch runtime state");
                }
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    if (!session) return <EmptyState title="Authentication Required" description="Please sign in to view runtime state." />;

    return (
        <OpsErrorBoundary>
            <div className="h-[calc(100vh-12rem)] max-w-4xl mx-auto p-4">
                <OpsEndpointCard
                    title="Runtime State Snapshot"
                    endpoint="/ops/retrieval/runtime_snapshot"
                    lastUpdated={data?.timestamp}
                    onRefresh={fetchData}
                    isLoading={loading}
                    error={error && error !== "Waiting for first query" ? error : null}
                >
                    {loading ? (
                        <LoadingSpinner fullHeight text="Loading runtime snapshot..." />
                    ) : error === "Waiting for first query" ? (
                        <EmptyState
                            icon={Camera}
                            title="No Snapshot Available"
                            description="The system has not processed any queries yet. A runtime snapshot will be captured after the first query."
                        />
                    ) : data ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold border-b pb-2">Effective Thresholds</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <DetailItem label="Semantic" value={data.semantic_threshold.toFixed(2)} />
                                    <DetailItem label="Cosine" value={data.cosine_threshold.toFixed(2)} />
                                    <DetailItem label="Min Chunks" value={data.min_chunks} />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold border-b pb-2">Reranking Strategy</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <DetailItem label="Enabled" value={data.rerank_enabled ? "Yes" : "No"} />
                                    <DetailItem label="Fallback Mode" value={data.rerank_fallback} />
                                </div>
                            </div>

                            <div className="space-y-4 md:col-span-2">
                                <h3 className="text-lg font-semibold border-b pb-2">Context</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <DetailItem label="Applied Policy" value={data.policy_applied} highlight />
                                    <DetailItem label="Detected Intent" value={data.intent_applied || "None"} />
                                    <div className="flex flex-col">
                                        <span className="text-muted-foreground text-sm">Captured At</span>
                                        <TimestampDisplay timestamp={data.timestamp} showIcon={false} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <EmptyState title="No Data" description="Unable to retrieve runtime state." />
                    )}
                </OpsEndpointCard>
            </div>
        </OpsErrorBoundary>
    );
}

const DetailItem = ({ label, value, highlight = false }: { label: string, value: string | number, highlight?: boolean }) => (
    <div className="flex flex-col">
        <span className="text-muted-foreground text-sm">{label}</span>
        <span className={`font-mono font-medium ${highlight ? 'text-primary' : ''}`}>{value}</span>
    </div>
);
